import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import API from "@/lib/api";

/**
 * Compact FAQ accordion shown inside the landing page hero section.
 *
 * Pulls the first N entries from `GET /api/faq` (admin-editable). Falls
 * back to nothing if the endpoint is unreachable so a transient backend
 * issue never breaks the landing page.
 */
export default function LandingFaqList({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let live = true;
    API.get("/faq")
      .then(({ data }) => { if (live) setItems((data || []).slice(0, limit)); })
      .catch(() => { /* fail-open: empty */ });
    return () => { live = false; };
  }, [limit]);

  if (items.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="landing-faq-list">
      {items.map((it) => {
        const isOpen = open === it.id;
        return (
          <div
            key={it.id}
            className={`rounded-2xl border bg-white transition-all ${isOpen ? "border-purple-300 shadow-md" : "border-slate-200 shadow-sm hover:border-slate-300"}`}
            data-testid={`landing-faq-${it.id}`}
          >
            <button
              type="button"
              className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4"
              onClick={() => setOpen(isOpen ? null : it.id)}
              aria-expanded={isOpen}
            >
              <span className="font-semibold text-base text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {it.question}
              </span>
              <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180 text-purple-600" : ""}`} />
            </button>
            {isOpen && (
              <div className="px-5 sm:px-6 pb-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {it.answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
